/**
 * Materials API Service - SUPABASE
 * =================================
 * All data fetched from Supabase database.
 * NO mock data. NO external API calls.
 * 
 * Tables:
 * - materials (id, name, image_url, dominant_color, secondary_colors, pattern, category, is_featured)
 * - projects (id, name, image, updated_at, user_id)
 */

import { supabase, isSupabaseConfigured } from '../lib/supabase';

// ============== MATERIALS ==============

/**
 * Get all materials with optional filters
 * SELECT * FROM materials
 */
export const getMaterials = async (filters = {}) => {
  console.log('📦 getMaterials() - Supabase');
  console.log('📍 Filters:', filters);
  
  if (!isSupabaseConfigured || !supabase) {
    console.warn('⚠️ Supabase not configured - returning empty data');
    return { success: true, data: [], total: 0 };
  }
  
  try {
    let query = supabase.from('materials').select('*');
    
    // Apply filters
    if (filters.category) {
      query = query.eq('category', filters.category);
    }
    if (filters.dominant_color) {
      query = query.eq('dominant_color', filters.dominant_color);
    }
    if (filters.pattern) {
      query = query.eq('pattern', filters.pattern);
    }
    if (filters.is_featured) {
      query = query.eq('is_featured', true);
    }
    if (filters.search) {
      query = query.ilike('name', `%${filters.search}%`);
    }
    if (filters.limit) {
      query = query.limit(filters.limit);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('❌ Supabase error:', error);
      return { success: false, data: [], error: error.message };
    }
    
    console.log('✅ Materials fetched:', data?.length || 0);
    
    return {
      success: true,
      data: data || [],
      total: data?.length || 0
    };
  } catch (err) {
    console.error('❌ getMaterials error:', err);
    return { success: false, data: [], error: err.message };
  }
};

/**
 * Get single material by ID, slug, or source_id
 * Uses Backend API which handles multiple lookup strategies
 */
export const getMaterialById = async (id) => {
  console.log('📦 getMaterialById() - Backend API');
  console.log('📍 Material ID/Slug:', id);
  
  if (!id) {
    return { success: false, error: 'No material ID provided' };
  }
  
  try {
    // Use Backend API - it handles slug, source_id, and UUID lookups
    const API = process.env.REACT_APP_BACKEND_URL || '';
    const response = await fetch(`${API}/api/materials/${id}`);
    
    if (!response.ok) {
      console.error('❌ Backend error:', response.status);
      return { success: false, error: `Material not found (${response.status})` };
    }
    
    const data = await response.json();
    console.log('✅ Material found:', data?.name);
    
    // Normalize data for frontend components
    return {
      success: true,
      data: {
        ...data,
        id: data.material_id || data.id,
        image_main: data.image_url, // R2 URL
        stone_type: data.stone_type || data.category,
        color_hex: getColorHex(data.color),
        finishes: data.finishes || ['Polished', 'Honed', 'Brushed'],
        applications: data.applications || ['Countertops', 'Flooring', 'Wall Cladding']
      }
    };
  } catch (err) {
    console.error('❌ getMaterialById error:', err);
    return { success: false, error: err.message };
  }
};

// Helper to convert color name to hex
const getColorHex = (colorName) => {
  const colorMap = {
    white: '#F5F5F5',
    beige: '#D4B896',
    cream: '#FFFDD0',
    black: '#2A2A2A',
    grey: '#9E9E9E',
    gray: '#9E9E9E',
    brown: '#8B6914',
    green: '#4CAF50',
    blue: '#4A90D9',
    gold: '#D4AF37',
    red: '#F44336',
    pink: '#E91E63',
    orange: '#FF9800',
    yellow: '#FFEB3B',
    purple: '#9C27B0',
    teal: '#008080'
  };
  return colorMap[colorName?.toLowerCase()] || '#666666';
};

/**
 * Get materials by dominant color
 */
export const getMaterialsByColor = async (color) => {
  return getMaterials({ dominant_color: color });
};

/**
 * Get featured materials
 */
export const getFeaturedMaterials = async (limit = 10) => {
  return getMaterials({ is_featured: true, limit });
};

/**
 * Get unique colors from materials
 * SELECT DISTINCT dominant_color FROM materials
 */
export const getColors = async () => {
  console.log('📦 getColors() - Supabase');
  
  if (!isSupabaseConfigured || !supabase) {
    console.warn('⚠️ Supabase not configured - returning empty data');
    return { success: true, data: [] };
  }
  
  try {
    const { data, error } = await supabase
      .from('materials')
      .select('dominant_color')
      .not('dominant_color', 'is', null);
    
    if (error) {
      console.error('❌ Supabase error:', error);
      return { success: false, data: [] };
    }
    
    // Get unique colors
    const uniqueColors = [...new Set(data?.map(m => m.dominant_color).filter(Boolean))];
    console.log('✅ Colors fetched:', uniqueColors.length);
    
    return {
      success: true,
      data: uniqueColors
    };
  } catch (err) {
    console.error('❌ getColors error:', err);
    return { success: false, data: [] };
  }
};

/**
 * Get unique categories from materials
 * SELECT DISTINCT category FROM materials
 */
export const getCategories = async () => {
  console.log('📦 getCategories() - Supabase');
  
  if (!isSupabaseConfigured || !supabase) {
    console.warn('⚠️ Supabase not configured - returning empty data');
    return { success: true, data: [] };
  }
  
  try {
    const { data, error } = await supabase
      .from('materials')
      .select('category')
      .not('category', 'is', null);
    
    if (error) {
      console.error('❌ Supabase error:', error);
      return { success: false, data: [] };
    }
    
    // Get unique categories
    const uniqueCategories = [...new Set(data?.map(m => m.category).filter(Boolean))];
    console.log('✅ Categories fetched:', uniqueCategories.length);
    
    return {
      success: true,
      data: uniqueCategories
    };
  } catch (err) {
    console.error('❌ getCategories error:', err);
    return { success: false, data: [] };
  }
};

/**
 * Get unique patterns from materials
 */
export const getPatterns = async () => {
  console.log('📦 getPatterns() - Supabase');
  
  if (!isSupabaseConfigured || !supabase) {
    console.warn('⚠️ Supabase not configured - returning empty data');
    return { success: true, data: [] };
  }
  
  try {
    const { data, error } = await supabase
      .from('materials')
      .select('pattern')
      .not('pattern', 'is', null);
    
    if (error) {
      console.error('❌ Supabase error:', error);
      return { success: false, data: [] };
    }
    
    const uniquePatterns = [...new Set(data?.map(m => m.pattern).filter(Boolean))];
    console.log('✅ Patterns fetched:', uniquePatterns.length);
    
    return {
      success: true,
      data: uniquePatterns
    };
  } catch (err) {
    console.error('❌ getPatterns error:', err);
    return { success: false, data: [] };
  }
};

/**
 * Search materials by name
 */
export const searchMaterials = async (query) => {
  return getMaterials({ search: query });
};

// ============== PROJECTS ==============

/**
 * Get all projects
 * SELECT * FROM projects ORDER BY updated_at DESC
 * No user_id filter - returns all projects
 */
export const getProjects = async (limit = 20) => {
  console.log('📦 getProjects() - Supabase');
  
  if (!isSupabaseConfigured || !supabase) {
    console.warn('⚠️ Supabase not configured - returning empty data');
    return { success: true, data: [] };
  }
  
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .order('updated_at', { ascending: false });
    
    console.log("PROJECTS RAW:", data);
    console.log("ERROR:", error);
    
    if (error) {
      console.error('❌ Supabase error:', error);
      return { success: false, data: [], error: error.message };
    }
    
    console.log('✅ Projects fetched:', data?.length || 0);
    
    return { success: true, data: data || [] };
  } catch (err) {
    console.error('❌ getProjects error:', err);
    return { success: false, data: [], error: err.message };
  }
};

/**
 * Get single project by ID
 * SELECT * FROM projects WHERE id = ?
 */
export const getProjectById = async (projectId) => {
  console.log('📦 getProjectById() - Supabase');
  console.log('📍 Project ID:', projectId);
  
  if (!isSupabaseConfigured || !supabase) {
    console.warn('⚠️ Supabase not configured - returning empty data');
    return { success: false, error: 'Supabase not configured' };
  }
  
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();
    
    if (error) {
      console.error('❌ Supabase error:', error);
      return { success: false, error: error.message };
    }
    
    console.log('✅ Project found:', data?.name);
    
    return { success: true, data };
  } catch (err) {
    console.error('❌ getProjectById error:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Get materials linked to a project
 * Uses Supabase foreign key alias syntax
 */
export const getProjectMaterials = async (projectId) => {
  console.log("🔥 LOADING MATERIALS FOR:", projectId);
  
  if (!isSupabaseConfigured || !supabase) {
    console.warn('⚠️ Supabase not configured');
    return [];
  }
  
  try {
    const { data, error } = await supabase
      .from('project_materials')
      .select(`
        material_id,
        materials:material_id (
          id,
          name,
          image_url,
          category
        )
      `)
      .eq('project_id', projectId);

    console.log("🔥 MATERIALS RAW:", data);
    console.log("🔥 MATERIALS ERROR:", error);

    return data?.map(item => item.materials) || [];
    
  } catch (err) {
    console.log("🔥 CATCH ERROR:", err);
    return [];
  }
};

/**
 * Get recent projects for dashboard
 * SELECT * FROM projects ORDER BY updated_at DESC
 */
export const getRecentProjects = async () => {
  console.log("🔥 FUNCTION START");

  try {
    const response = await supabase
      .from('projects')
      .select('*')
      .order('updated_at', { ascending: false });

    console.log("🔥 FULL RESPONSE:", response);

    if (!response) {
      console.log("🔥 NO RESPONSE");
      return [];
    }

    if (response.error) {
      console.log("🔥 ERROR:", response.error);
      return [];
    }

    console.log("🔥 DATA:", response.data);

    return response.data || [];

  } catch (err) {
    console.log("🔥 CATCH ERROR:", err);
    return [];
  }
};

/**
 * Get projects by user ID
 */
export const getProjectsByUser = async (userId, limit = 10) => {
  console.log('📦 getProjectsByUser() - Supabase');
  console.log('📍 User ID:', userId);
  
  if (!isSupabaseConfigured || !supabase) {
    console.warn('⚠️ Supabase not configured - returning empty data');
    return { success: true, data: [] };
  }
  
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('❌ Supabase error:', error);
      return { success: false, data: [] };
    }
    
    console.log('✅ User projects fetched:', data?.length || 0);
    
    return { success: true, data: data || [] };
  } catch (err) {
    console.error('❌ getProjectsByUser error:', err);
    return { success: false, data: [] };
  }
};

// ============== INSPIRATION ==============

/**
 * Get inspiration materials for dashboard
 * SELECT * FROM materials WHERE is_featured = true LIMIT ?
 */
export const getInspirationMaterials = async (limit = 4) => {
  console.log('📦 getInspirationMaterials() - Supabase');
  console.log('📍 Limit:', limit);
  
  if (!isSupabaseConfigured || !supabase) {
    console.warn('⚠️ Supabase not configured - returning empty data');
    return { success: true, data: [] };
  }
  
  try {
    const { data, error } = await supabase
      .from('materials')
      .select('*')
      .eq('is_featured', true)
      .limit(limit);
    
    if (error) {
      console.error('❌ Supabase error:', error);
      return { success: false, data: [], error: error.message };
    }
    
    // Transform to dashboard format
    const materials = (data || []).map(mat => ({
      id: mat.id,
      name: mat.name || 'Unknown',
      type: mat.category || 'Stone',
      image: mat.image_url || 'https://images.unsplash.com/photo-1558346648-9757f2fa4474?w=400&q=75'
    }));
    
    console.log('✅ Inspiration materials fetched:', materials.length);
    
    return { success: true, data: materials };
  } catch (err) {
    console.error('❌ getInspirationMaterials error:', err);
    return { success: false, data: [], error: err.message };
  }
};

// ============== HELPERS ==============

/**
 * Format relative time (e.g., "2h fa", "1g fa")
 */
const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Recente';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  
  if (diffMins < 60) return `${diffMins}m fa`;
  if (diffHours < 24) return `${diffHours}h fa`;
  if (diffDays < 7) return `${diffDays}g fa`;
  return `${diffWeeks}w fa`;
};

export default {
  getMaterials,
  getMaterialById,
  getMaterialsByColor,
  getFeaturedMaterials,
  getColors,
  getCategories,
  getPatterns,
  searchMaterials,
  getProjects,
  getProjectById,
  getProjectMaterials,
  getRecentProjects,
  getProjectsByUser,
  getInspirationMaterials
};
